import { Link } from 'react-router-dom';
import {
  Activity, TrendingUp, DollarSign, ShoppingBag, ArrowDownRight, ArrowUpRight,
  LineChart, BarChart3, PieChart, Filter, AlertTriangle, FileText, ChevronRight,
} from 'lucide-react';
import { useKpiHealth, useKpiSummary } from '../hooks/useKpi';
import { formatCurrency } from '../../../utils/format-currency';

const REPORTS = [
  { to: '/kpi/timeseries',   label: 'Series de tiempo', desc: 'Evolución de ventas por día/semana/mes', icon: LineChart,   color: 'text-sky-600 bg-sky-50' },
  { to: '/kpi/top',          label: 'Top',              desc: 'Ranking de productos, métodos, etc.',    icon: BarChart3,   color: 'text-emerald-600 bg-emerald-50' },
  { to: '/kpi/distribution', label: 'Distribución',     desc: 'Histograma de valores',                  icon: PieChart,    color: 'text-violet-600 bg-violet-50' },
  { to: '/kpi/funnel',       label: 'Embudo',           desc: 'Conversión por etapas',                  icon: Filter,      color: 'text-amber-600 bg-amber-50' },
  { to: '/kpi/anomalies',    label: 'Anomalías',        desc: 'Detección de picos y caídas',            icon: AlertTriangle, color: 'text-rose-600 bg-rose-50' },
  { to: '/kpi/docs',         label: 'Documentación',    desc: 'Referencia de la API de KPIs',           icon: FileText,    color: 'text-slate-600 bg-slate-100' },
];

function StatTile({ label, value, icon, accent }: { label: string; value: string; icon: React.ReactNode; accent?: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-text-muted">{label}</span>
        <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${accent ?? 'bg-muted text-text-muted'}`}>{icon}</span>
      </div>
      <p className="text-xl font-bold text-text">{value}</p>
    </div>
  );
}

export default function KpiDashboard() {
  const health = useKpiHealth();
  const summary = useKpiSummary({ collection: 'orders' });
  const s = summary.data;
  const online = health.data?.status === 'ok';

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text">KPIs</h1>
          <p className="mt-1 text-sm text-text-muted">Indicadores de ventas y accesos a informes detallados</p>
        </div>
        <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${online ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
          <span className={`h-2 w-2 rounded-full ${online ? 'bg-emerald-500' : 'bg-rose-500'} ${online ? 'animate-pulse' : ''}`} />
          {online ? 'Servicio operativo' : 'Servicio no disponible'}
        </span>
      </div>

      {/* Resumen de ventas (colección: pedidos pagados) */}
      <div>
        <div className="mb-3 flex items-center gap-2">
          <DollarSign size={16} className="text-primary" />
          <h2 className="text-sm font-semibold text-text">Resumen de ventas</h2>
          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-text-muted">pedidos pagados</span>
        </div>
        {summary.isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-muted" />)}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile label="Ingresos totales" value={formatCurrency(s?.total ?? 0)} icon={<DollarSign size={15} />} accent="bg-emerald-50 text-emerald-600" />
            <StatTile label="Ticket promedio" value={formatCurrency(s?.avg ?? 0)} icon={<TrendingUp size={15} />} accent="bg-sky-50 text-sky-600" />
            <StatTile label="Venta mínima" value={formatCurrency(s?.min ?? 0)} icon={<ArrowDownRight size={15} />} accent="bg-amber-50 text-amber-600" />
            <StatTile label="Venta máxima" value={formatCurrency(s?.max ?? 0)} icon={<ArrowUpRight size={15} />} accent="bg-violet-50 text-violet-600" />
          </div>
        )}
        {s?.count !== undefined && (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-text-muted">
            <ShoppingBag size={13} /> {s.count} pedido{s.count === 1 ? '' : 's'} pagado{s.count === 1 ? '' : 's'} en el periodo
          </p>
        )}
      </div>

      {/* Reportes */}
      <div>
        <div className="mb-3 flex items-center gap-2">
          <Activity size={16} className="text-primary" />
          <h2 className="text-sm font-semibold text-text">Informes detallados</h2>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {REPORTS.map(r => {
            const Icon = r.icon;
            return (
              <Link
                key={r.to}
                to={r.to}
                className="group flex items-center gap-3 rounded-xl border border-border bg-surface p-4 transition hover:border-primary/40 hover:shadow-sm"
              >
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${r.color}`}>
                  <Icon size={18} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-text">{r.label}</p>
                  <p className="truncate text-xs text-text-muted">{r.desc}</p>
                </div>
                <ChevronRight size={16} className="shrink-0 text-text-muted transition group-hover:translate-x-0.5 group-hover:text-primary" />
              </Link>
            );
          })}
        </div>
      </div>

      {health.data?.timestamp && (
        <p className="text-[11px] text-text-muted">Última verificación: {new Date(health.data.timestamp).toLocaleString()}</p>
      )}
    </div>
  );
}
